import { useState, useMemo } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { css } from 'styled-components';
import { format } from 'date-fns';
import { formatInstantInZone } from '../../helpers/timezoneHelpers';
import { useNavigate } from 'react-router-dom';
import { URLS } from '../../../constants/urls';
import { useGetCancelledBookingsAsHostQuery } from '../../services/bookingService/__generated__/queries.generated';
import { Dialog, DialogPanel } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { RefundStatus } from '../../../__generated__/graphql';
import { useExecuteRefundMutation } from '../../services/stripeService/__generated__/queries.generated';
import { useHostCapabilities } from '../../hooks/useHostCapabilities';
import { ManagedByBrandNote } from '../../components/ManagedByBrandNote';
import { toast } from 'react-toastify';
import { formatBookingLocationDisplay } from '../../helpers/formatAddress';
import { MyBookingsPageSkeleton } from '../../components/skeletons';

const PageContainer = styled.div`
  ${tw`w-full max-w-full min-w-0 box-border p-4 bg-bg-secondary min-h-screen pb-8 md:pb-16`}
`;

const Title = styled.h1`
  ${tw`lg:text-3xl text-2xl font-bold mb-6 text-text-primary px-4 md:px-8 lg:px-0 max-w-screen-xl mx-auto lg:py-5 md:py-3 py-2`}
`;

const BookingList = styled.div`
  ${tw`space-y-4 max-w-screen-xl mx-auto px-4 md:px-8 lg:px-0`}
`;

const BookingCard = styled.div`
  ${tw`bg-white rounded-lg shadow-card transition-shadow duration-200 cursor-pointer`}
  &:hover {
    ${tw`shadow-card-hover bg-bg-secondary border-solid border-border-default`}
  }
`;

const BookingHeader = styled.div`
  ${tw`p-4 border-b border-border-default flex justify-between items-center`}
`;

const BookingBody = styled.div`
  ${tw`p-4`}
`;

const DateRange = styled.div`
  ${tw`text-sm text-text-secondary mb-2`}
`;

const CarInfo = styled.div`
  ${tw`flex items-center gap-4`}
`;

const CarImage = styled.img`
  ${tw`w-20 h-16 rounded-md object-cover`}
`;

const CarDetails = styled.div`
  ${tw`flex-1`}
`;

const CarName = styled.h3`
  ${tw`text-lg font-medium`}
`;

const Location = styled.p`
  ${tw`text-sm text-text-secondary`}
`;

const Label = styled.span`
  ${tw`font-bold`}
`;

const Status = styled.span<{ status: string }>`
  ${tw`px-2 py-1 rounded-full text-xs font-medium max-w-full min-w-0 box-border`}
  ${({ status }) => status.includes('CANCELLED') && tw`bg-red-100 text-red-800`}
`;

const GuestInfo = styled.div`
  ${tw`mt-3 pt-3 border-t border-border-default`}
`;

const GuestLabel = styled.span`
  ${tw`text-sm text-text-secondary`}
`;

const GuestName = styled.span`
  ${tw`text-sm text-text-secondary ml-1`}
`;

const TabContainer = styled.div`
  ${tw`flex mb-6 justify-center rounded-md overflow-hidden`}
  background-color: var(--color-border);
  padding: 2px;
  width: fit-content;
  margin: 0 auto;
`;

const Tab = styled.button<{ active: boolean }>`
  ${tw`px-8 py-2 text-sm font-medium transition-all duration-200 border-none`}
  ${({ active }) =>
    active
      ? tw`bg-black text-white`
      : css`
          ${tw`bg-transparent text-text-secondary`}
          &:hover {
            ${tw`text-text-primary`}
          }
        `}
`;

const FilterContainer = styled.div`
  ${tw`flex justify-center mb-6 gap-4 mt-6`}
`;

const FilterSelect = styled.select`
  ${tw`px-3 py-2 border border-border-default rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black`}
`;

const NoBookingsMessage = styled.div`
  ${tw`text-center text-text-secondary mt-8 p-6 bg-white rounded-lg shadow-card`}
`;

// Modal components
const ModalOverlay = styled.div`
  ${tw`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40`}
`;

const ModalContent = styled.div`
  ${tw`bg-white rounded-lg p-6 w-full max-w-2xl mx-4 shadow-overlay`}
`;

const ModalHeader = styled.div`
  ${tw`flex justify-between items-center mb-4`}
`;

const ModalTitle = styled.h2`
  ${tw`text-xl font-bold`}
`;

const CloseButton = styled.button`
  ${tw`text-text-secondary focus:outline-none`}
  &:hover {
    ${tw`text-text-secondary`}
  }
`;

const RefundDetails = styled.div`
  ${tw`mt-4 p-4 bg-bg-secondary rounded-lg border border-border-default`}
`;

const RefundReasonInput = styled.textarea`
  ${tw`w-full mt-4 p-3 border border-border-default rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black`}
  min-height: 90px;
`;

const RefundItem = styled.div`
  ${tw`flex justify-between py-2 border-b border-border-default last:border-0`}
`;

const ActionButton = styled.button`
  ${tw`px-4 py-2 rounded-md focus:outline-none transition-colors duration-200`}
`;

const ConfirmRefundBtn = styled(ActionButton)`
  ${tw`bg-green-200 text-green-800 border-black cursor-pointer`}
  &:hover {
    ${tw`bg-green-300`}
  }
`;

const ViewDetailsButton = styled(ActionButton)`
  ${tw`bg-black text-white cursor-pointer`}
  &:hover {
    ${tw`bg-blue-700`}
  }
`;

const RefundTitle = styled.h3`
  ${tw`font-bold mb-2`}
`;

const RefundStatusText = styled.span<{ completed: boolean }>`
  ${tw`font-medium`}
  ${({ completed }) => (completed ? tw`text-green-600` : tw`text-yellow-600`)}
`;

const ModalFooter = styled.div`
  ${tw`mt-6 flex justify-end gap-4`}
`;

const HostCancelledBookingsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<
    'all' | 'pending' | 'refunded' | 'processing' | 'failed'
  >('all');
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [refundReason, setRefundReason] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { visible: canHost } = useHostCapabilities();
  const [executeRefund, { loading: executeRefundLoading }] = useExecuteRefundMutation({
    onCompleted: async () => {
      toast.success('Refund executed successfully');
      // Refetch cancelled bookings to update the UI
      await refetch();
      closeModal();
    },
    onError: error => {
      toast.error(`Failed to execute refund: ${error.message}`);
    },
  });

  const {
    data: hostCancelledData,
    loading: hostLoading,
    error: hostError,
    refetch,
  } = useGetCancelledBookingsAsHostQuery({
    variables: {
      filter: selectedReason ? { cancellationReason: selectedReason as any } : {},
    },
  });

  // Helper function to find refund invoice from booking
  const getRefundInvoice = (booking: any) => {
    if (!booking?.invoices) return null;
    return booking.invoices.find(
      (inv: any) => inv.type === 'FULL_REFUND' || inv.type === 'PARTIAL_WITHIN_24_HOURS_REFUND',
    );
  };

  const filteredBookings = useMemo(() => {
    if (!hostCancelledData?.cancelledBookingsAsHost?.length) return [];

    return hostCancelledData.cancelledBookingsAsHost.filter(booking => {
      const refundInvoice = getRefundInvoice(booking);

      switch (activeTab) {
        case 'all':
          return true;
        case 'pending':
          return (
            refundInvoice &&
            (refundInvoice.refundStatus === 'PENDING_HOST_REVIEW' ||
              refundInvoice.refundStatus === 'PROCESSING')
          );
        case 'refunded':
          return refundInvoice && refundInvoice.refundStatus === 'COMPLETED';
        default:
          return true;
      }
    });
  }, [hostCancelledData?.cancelledBookingsAsHost, activeTab]);

  const handleBookingClick = (booking: any) => {
    setSelectedBooking(booking);
    setRefundReason('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedBooking(null);
    setRefundReason('');
  };

  const navigateToBookingDetails = (bookingId: string) => {
    navigate(`/cars/host/booking/${bookingId}`);
  };

  const executeRefundClicked = async () => {
    if (!selectedBooking) return;

    const refundInvoice = getRefundInvoice(selectedBooking);
    if (!refundInvoice) {
      toast.error('Refund invoice not found');
      return;
    }

    if (refundInvoice.refundStatus !== 'PENDING_HOST_REVIEW') {
      toast.error('Refund is not pending host review');
      return;
    }

    try {
      await executeRefund({
        variables: {
          input: {
            bookingId: selectedBooking.id,
            invoiceId: refundInvoice.id,
            amount: Math.abs(refundInvoice.amount),
            reason: refundReason.trim() || undefined,
          },
        },
      });
      // Close modal and refresh data are handled in onCompleted
    } catch (error) {
      // Error handling is done in mutation's onError
    }
  };

  if (hostLoading) {
    return <MyBookingsPageSkeleton />;
  }

  if (hostError) {
    return <PageContainer>Error loading cancelled bookings.</PageContainer>;
  }

  return (
    <PageContainer>
      <Title>Cancelled Trips</Title>

      <TabContainer>
        <Tab active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
          All
        </Tab>
        <Tab active={activeTab === 'pending'} onClick={() => setActiveTab('pending')}>
          Pending Refund
        </Tab>
        <Tab active={activeTab === 'refunded'} onClick={() => setActiveTab('refunded')}>
          Refunded
        </Tab>
      </TabContainer>

      <FilterContainer>
        <FilterSelect value={selectedReason} onChange={e => setSelectedReason(e.target.value)}>
          <option value="">All Reasons</option>
          <option value="GUEST_REQUESTED">Guest Requested</option>
          <option value="HOST_CANCELLED">Host Cancelled</option>
          <option value="CAR_UNAVAILABLE">Car Unavailable</option>
          <option value="EMERGENCY">Emergency</option>
          <option value="WEATHER">Weather</option>
          <option value="SYSTEM_CANCELLED">System Cancelled</option>
          <option value="OTHER">Other</option>
        </FilterSelect>
      </FilterContainer>

      {!filteredBookings.length ? (
        <NoBookingsMessage>No cancelled bookings found.</NoBookingsMessage>
      ) : (
        <BookingList>
          {filteredBookings.map(booking => (
            <BookingCard key={booking.id} onClick={() => handleBookingClick(booking)}>
              <BookingHeader>
                <Status status={booking.status}>{booking.status.replace(/_/g, ' ')}</Status>
                <span className="text-sm text-gray-500">
                  {booking.cancellationReason?.replace(/_/g, ' ')}
                </span>
              </BookingHeader>
              <BookingBody>
                <DateRange>
                  {formatInstantInZone(
                    booking.startAt,
                    booking.car?.location?.timezone ?? 'America/Los_Angeles',
                    'MM/dd/yyyy hh:mm a',
                  )}{' '}
                  -{' '}
                  {formatInstantInZone(
                    booking.endAt,
                    booking.car?.location?.timezone ?? 'America/Los_Angeles',
                    'MM/dd/yyyy hh:mm a',
                  )}
                </DateRange>
                <CarInfo>
                  <CarImage
                    src={
                      `${URLS.S3_BUCKET.CAR_THUMBNAILS}/${booking.car?.thumbnailUrl}` ||
                      '/placeholder-car.png'
                    }
                    alt="Car"
                  />
                  <CarDetails>
                    <CarName>{`${booking.car?.year} ${booking.car?.make} ${booking.car?.model}`}</CarName>
                    <Location>
                      <Label>Pickup location:</Label>{' '}
                      {formatBookingLocationDisplay(booking.pickupLocation, booking.car?.location)}
                    </Location>
                    <Location>
                      <Label>Return location:</Label>{' '}
                      {formatBookingLocationDisplay(booking.returnLocation, booking.car?.location)}
                    </Location>
                    <GuestInfo>
                      <GuestLabel>Guest:</GuestLabel>
                      <GuestName>
                        {booking.guestDisplayName ||
                          booking.guest?.displayName ||
                          `${booking.guest?.firstName} ${booking.guest?.lastName}` ||
                          'Deleted User'}
                      </GuestName>
                    </GuestInfo>
                  </CarDetails>
                </CarInfo>
              </BookingBody>
            </BookingCard>
          ))}
        </BookingList>
      )}

      {isModalOpen && selectedBooking && (
        <Dialog open={isModalOpen} onClose={closeModal} as={ModalOverlay}>
          <DialogPanel as={ModalContent}>
            <ModalHeader>
              <ModalTitle>Cancellation Details</ModalTitle>
              <CloseButton onClick={closeModal}>
                <FontAwesomeIcon icon={faTimes} />
              </CloseButton>
            </ModalHeader>

            <div>
              <p>
                <strong>Status:</strong> {selectedBooking.status.replace(/_/g, ' ')}
              </p>
              <p>
                <strong>Reason:</strong>{' '}
                {selectedBooking.cancellationReason?.replace(/_/g, ' ') || 'Not specified'}
              </p>
              <p>
                <strong>Cancelled at:</strong>{' '}
                {selectedBooking.cancelledInitiatedAt
                  ? format(new Date(selectedBooking.cancelledInitiatedAt), 'MM/dd/yyyy hh:mm a')
                  : 'Not available'}
              </p>

              <RefundDetails>
                <RefundTitle>Refund Information</RefundTitle>
                {(() => {
                  const refundInvoice = getRefundInvoice(selectedBooking);
                  const refundAmount = refundInvoice ? Math.abs(refundInvoice.amount) : 0;
                  const penaltyAmount = refundInvoice?.breakdown?.penaltyAmount || 0;
                  const refundStatus = refundInvoice?.refundStatus || 'NONE';

                  return (
                    <>
                      <RefundItem>
                        <span>Refund Status:</span>
                        <RefundStatusText completed={refundStatus === 'COMPLETED'}>
                          {refundStatus}
                        </RefundStatusText>
                      </RefundItem>
                      <RefundItem>
                        <span>Refund Amount:</span>
                        <span>${refundAmount.toFixed(2)}</span>
                      </RefundItem>
                      <RefundItem>
                        <span>Penalty Amount:</span>
                        <span>${penaltyAmount.toFixed(2)}</span>
                      </RefundItem>
                    </>
                  );
                })()}
              </RefundDetails>

              {(() => {
                const refundInvoice = getRefundInvoice(selectedBooking);
                const canExecuteRefund =
                  refundInvoice && refundInvoice.refundStatus === 'PENDING_HOST_REVIEW';

                if (!canExecuteRefund) {
                  return null;
                }

                return (
                  <div>
                    <label tw="block text-sm font-medium text-text-secondary mt-4 mb-1">
                      Refund reason (optional)
                    </label>
                    <RefundReasonInput
                      value={refundReason}
                      onChange={e => setRefundReason(e.target.value)}
                      placeholder="Add a short note for the guest or internal records"
                    />
                  </div>
                );
              })()}

              <ModalFooter>
                {(() => {
                  const refundInvoice = getRefundInvoice(selectedBooking);
                  const canExecuteRefund =
                    refundInvoice && refundInvoice.refundStatus === 'PENDING_HOST_REVIEW';

                  return (
                    <>
                      {canExecuteRefund && !canHost('refundReview') && (
                        <ManagedByBrandNote capability="refundReview" />
                      )}
                      {canExecuteRefund && canHost('refundReview') && (
                        <ConfirmRefundBtn
                          onClick={executeRefundClicked}
                          disabled={executeRefundLoading}
                        >
                          {executeRefundLoading ? 'Processing...' : 'Execute the Refund Now'}
                        </ConfirmRefundBtn>
                      )}
                      <ViewDetailsButton
                        onClick={() => navigateToBookingDetails(selectedBooking.id)}
                      >
                        View Full Details
                      </ViewDetailsButton>
                    </>
                  );
                })()}
              </ModalFooter>
            </div>
          </DialogPanel>
        </Dialog>
      )}
    </PageContainer>
  );
};

export default HostCancelledBookingsPage;
